/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import Globals from "../../shared/Globals";
import { qreq } from "../../shared/qrequest";
import AppActions from "../AppActions";
import L from "../Lang"
import BaseCom from "../BaseCom";
import Card from "../Card";
import DiagCodeSelect from "../DiagCodeSelect";
import FormInput from "../FormInput";
import Icon from "../Icon";
import InsuranceList from "../InsuranceList";
import LoadingAlert from "../LoadingAlert";
import Patient from "../Patient";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import CollectionInfo from "./CollectionInfo";
import PanelSelect from "./PanelSelect";
import { Tab, Tabs } from "react-bootstrap";
import SpecimenSummary from "./SpecimenSummary";
import { Link } from "react-router-dom";

class SpecimenEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            tab: 'summary',
            item: {
                patient: null
            },
            specimen: {
                panels: []
            },
            accounts: []
        }
    }

    async componentDidMount() {
        await this.getSettings();
        await this.fillListAsync('/api/account/list', 'accounts');
        await this.loadAsync();
        this.inferTab();
        this.setState({ loaded: true });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.edit !== this.props.edit) {
            this.loadAsync();
            this.inferTab();
        }
    }

    inferTab = () => {
        let tab = 'summary';
        if (this.props.edit) {
            if (this.props.params.id)
                tab = 'patient';
            else
                tab = 'requisition';
        }
        this.setState({ tab: tab });
    }

    loadAsync = async () => {
        if (this.props.params.id) {
            try {
                let j = await qreq.getAsync('/api/specimen/get/' + this.props.params.id);
                if (j.item) {
                    this.setState({ specimen: { ...j.item, collectedTime: Globals.getTimeOnly(Globals.toISODateString(j.item.collectedDate)) }, item: j.item.order });
                    await this.selectAccount(j.item.accountID, true);
                }
                else
                    this.alert(j.errorMessage);
            }
            catch {
                this.unkownErrorCallback();
            }
        }
    }

    change = () => {
        this.setState({ item: { ...this.state.item }, specimen: { ...this.state.specimen } });
    }

    selectAccount = async (accountID, noChange) => {
        if (!noChange) this.change();
        if (accountID === undefined || accountID === null) accountID = 0;
        accountID = Number(accountID);
        try {
            const j = await qreq.postAsync('/api/provider/list', { accountID: accountID });
            if (j.list) {
                this.setState({ providers: j.list });
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    newPatient = () => {
        this.setState({ item: { ...this.state.item, patient: {}, insurances: [] } });
    }

    selectPatient = async (patient) => {
        if (!patient) {
            this.setState({ item: { ...this.state.item, patient: null, patientID: 0 } });
            return;
        }
        try {
            let j = await qreq.getAsync('/api/patient/get/' + patient.id);
            let item = { ...this.state.item };
            if (j.item) {
                item.patient = j.item;
                item.patientID = j.item.id;
                item.insurances = [];

                j = await qreq.getAsync('/api/order/patient/' + patient.id);
                if (j.errorCode === 0) {
                    item.insurances = j.item.insurances;
                    this.setState({ item: item });
                }
                else
                    this.alert(j.errorMessage);
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback();
        }
    }

    mergeDateTime(date, time) {
        if (date.indexOf('T') >= 0)
            date = date.split('T')[0];
        else if (date.indexOf(' ') >= 0)
            date = date.split(' ')[0];
        let str = (date + ' ' + time).replace('Z', '');
        return Globals.toISODateString(new Date(str));
    }

    submit = async (e) => {
        e?.preventDefault();
        if (!this.props.edit) return;
        if (!this.state.item.accountID) {
            this.alert('Please select an account');
            return;
        }
        if (!this.state.specimen.providerID) {
            this.alert('Please select an provider');
            return;
        }
        if (!this.state.item.patient) {
            this.alert('Please select a patient');
            return;
        }
        if (this.state.specimen.panels.length === 0) {
            this.alert('Please select a panel');
            return;
        }

        try {
            let collectedDateStr = this.state.specimen.collectedDateStr;
            if (!collectedDateStr)
                collectedDateStr = Globals.toISODateString(new Date());
            let data = {
                ...this.state.specimen,
                order: { ...this.state.item },
                collectedDate: this.state.specimen.isCollected ? this.mergeDateTime(collectedDateStr, this.state.specimen.collectedTime) : new Date()
            };
            let j = await qreq.postAsync('/api/specimen/save', data);

            if (j.errorCode === 0) {
                this.success('Specimen saved');
                this.goBack();
            }
            else
                this.alert(j.errorMessage);

        }
        catch (e) {
            this.alert(e);
            return;
        }
    }

    render() {
        if (!this.state.loaded)
            return <LoadingAlert />
        return <div className="container-fluid vh-expand">
            <div className="p-4"></div>
            {!this.state.specimen && <h1>{this.props.edit ? (this.props.params.id ? <L>Edit Specimen</L> : <L>New Specimen</L>) : <L>Specimen</L>}</h1>}
            {this.state.specimen && <h1>#{this.state.specimen.sid} ({this.T('specimen')})</h1>}
            <form onSubmit={this.submit}>
                <AppActions>
                    {!this.props.edit && <Link to="edit" role="button" className="btn btn-primary btn-lg"><Icon icon="faPencil" /> <L>Edit</L></Link>}
                    {this.props.edit && <button type="submit" className="btn btn-success btn-lg"><Icon icon="faSave" /> <L>Save</L></button>}
                </AppActions>

                <Tabs
                    activeKey={this.state.tab}
                    defaultActiveKey={this.state.tab}
                    onSelect={(k) => { this.setState({ tab: k }); }}
                    id="c-specimen-tabs"
                    className="app-tabs"
                    animation="false">
                    {!this.props.edit && <Tab eventKey="summary" title={<L>Summary</L>}>
                        <SpecimenSummary specimen={this.state.specimen} order={this.state.item} />
                    </Tab>}
                    {this.props.edit && <Tab eventKey="requisition" title={<L>Requisition</L>}>
                        <Card title="Requisition">
                            <FormInput model={this.state.item} name="accountID" label="Account" type="select" options={this.state.accounts} onChange={this.selectAccount} disabledView={!this.props.edit} />
                            {this.state.item.accountID &&
                                (this.state.providers ? <FormInput model={this.state.specimen} name="providerID" label="Provider" type="select" options={this.state.providers} onChange={this.change} disabledView={!this.props.edit} />
                                    : <LoadingAlert />)}
                        </Card>
                    </Tab>}
                    {this.state.item.accountID &&
                        <Tab eventKey="patient" title={<L>Patient</L>}>
                            {this.props.edit && !this.state.item.patient && <>
                                <Card title={this.T('subject')}>
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <FormInput model={this.state.item} name="patientID" label={this.T('subject')} type="search" searchUrl="/api/patient/list?includeLeads=true" onChange={this.selectPatient} required disabledView={!this.props.edit} />
                                        </div>
                                        <div className="col-md-1 text-center">
                                            <div className="p-2"><L>OR</L></div>
                                        </div>
                                        <div className="col-md text-center text-md-start">
                                            <button type="button" className="btn btn-primary mt-3" onClick={this.newPatient}><Icon icon="faPlus" /> New {this.T('subject')}</button>
                                        </div>
                                    </div>
                                </Card>
                            </>}
                            {this.state.item.patient && <>
                                {this.props.edit && <Card><button type="button" className="btn btn-secondary" onClick={() => this.setState({ item: { ...this.state.item, patient: null, patientID: 0 } })}>Change {this.T('subject')}</button></Card>}
                                <Patient title={this.T('subject')} item={this.state.item.patient} requestAddress requestContact readOnly={!this.props.edit} />
                                <InsuranceList list={this.state.item.insurances} edit={this.props.edit} />
                            </>}
                        </Tab>}
                    {this.state.item.patient &&
                        <Tab eventKey="panels" title={<L>Panels</L>}>
                            <PanelSelect item={this.state.specimen} onChange={this.change} readOnly={!this.props.edit} />
                        </Tab>}
                    {this.state.item.patient && <Tab eventKey="collection" title={<L>Collection</L>}>
                        <CollectionInfo item={this.state.specimen} onChange={this.change} readOnly={!this.props.edit} />
                        <Card title="Notes">
                            <FormInput model={this.state.item} name="collectionNotes" label="Collection Notes" type="textarea" onChange={this.change} disabledView={!this.props.edit} />
                        </Card>
                    </Tab>}
                    {this.state.item.patient && <Tab eventKey="coding" title={<L>Clinical Codes</L>}>
                        <DiagCodeSelect item={this.state.specimen} onChange={this.change} disabled={!this.props.edit} />
                    </Tab>}
                </Tabs>

            </form>
        </div>;
    }
}

export default withParamsAndNavigate(SpecimenEdit);